<script setup lang="ts">
    import { InputRow, Combobox } from '@/components/ui/form';
    import { Input } from '@/components/ui/input';
    import { computed, ref } from 'vue';
    import { usePage } from '@inertiajs/vue3';
    import type { SharedData, SelectOption, Company } from '@/types';

    const page = usePage<SharedData>();
    const isClient = computed(() => page.props.auth.user.is_client);

    const props = defineProps<{
        form: any;
        company: Company;
        productionUnitsData: any;
        departmentsData: any;
        companiesData: any;
    }>();

    const productionUnitOptions = computed(() => {
        const types = props.productionUnitsData ?? [];

        return Object.entries(types).map(([key, label]) => ({
            value: key,
            label: label as string,
        }));
    });

    const departmentsOptions = computed(() => {
        const types = props.departmentsData ?? [];

        return Object.entries(types).map(([key, label]) => ({
            value: key,
            label: label as string,
        }));
    });

    const companiesOptions = computed(() => {
        const types = props.companiesData ?? [];

        return Object.entries(types).map(([key, label]) => ({
            value: Number(key),
            label: label as string,
        }));
    });

    const applyMask = (e: Event) => {
        const input = e.target as HTMLInputElement;
        let val = input.value.replace(/\D/g, ''); // remove non-digits
        if (val.length > 6) {
            val = val.slice(0, 6) + '-' + val.slice(6, 10);
        }
        props.form.customer_employees.cpr = val;
    };
</script>

<template>
    <div class="grid gap-3">
        <InputRow
            :form="form"
            name="first_name"
            label="employee.personal.first_name"
            error-key="customer_employees.first_name"
        >
            <Input
                id="first_name"
                v-model="form.customer_employees.first_name"
                :placeholder="$t('employee.personal.first_name')"
            />
        </InputRow>
        <InputRow
            :form="form"
            name="last_name"
            label="employee.personal.last_name"
            error-key="customer_employees.last_name"
        >
            <Input
                id="last_name"
                v-model="form.customer_employees.last_name"
                :placeholder="$t('employee.personal.last_name')"
            />
        </InputRow>
        <InputRow
            :form="form"
            name="cpr"
            label="employee.personal.cpr"
            error-key="customer_employees.cpr"
        >
            <Input
                id="cpr"
                v-model="form.customer_employees.cpr"
                placeholder="ddmmyy-xxxx"
                @input="applyMask"
            />
        </InputRow>
        <InputRow
            :form="form"
            name="email"
            label="employee.personal.email"
            error-key="customer_employees.email"
        >
            <Input
                id="email"
                v-model="form.customer_employees.email"
                :placeholder="$t('employee.personal.email')"
            />
        </InputRow>
        <InputRow
            v-if="!isClient"
            :form="form"
            name="company_id"
            label="employee.personal.company_id"
        >
            <Combobox
                v-model="form.customer_employees.company_id"
                :options="companiesOptions"
                :placeholder="$t('employee.personal.company_id')"
                :show-remove-button="false"
            />
        </InputRow>
        <InputRow
            :form="form"
            name="phone_cell"
            label="employee.personal.phone_cell"
        >
            <Input
                id="phone_cell"
                v-model="form.customer_employees.phone_cell"
                :placeholder="$t('employee.personal.phone_cell')"
            />
        </InputRow>
        <InputRow
            v-if="departmentsOptions.length > 0"
            :form="form"
            name="department"
            label="employee.personal.department"
        >
            <Combobox
                v-model="form.customer_employees.department"
                :options="departmentsOptions"
                :placeholder="$t('employee.personal.department')"
            />
        </InputRow>
        <InputRow
            v-else
            :form="form"
            name="department"
            label="employee.personal.department"
        >
            <Input
                id="department"
                v-model="form.customer_employees.department"
                :placeholder="$t('employee.personal.department')"
            />
        </InputRow>

        <InputRow
            v-if="company.employees_optional?.production_unit"
            :form="form"
            name="production_unit"
            label="employee.optional.production_unit"
        >
            <Combobox
                v-model="form.optional.production_unit"
                :options="productionUnitOptions"
                :placeholder="$t('employee.optional.production_unit')"
            />
        </InputRow>
    </div>
</template>
